// hooks
import { useStore } from '@/store';
import { useAxios } from '@/vue3/hooks/axiosHook';
import { PinzoneGeom, GreenZonesLayers, SharedGreenGeom } from '../models';

export const usePinzoneService = () => {
    // hooks
    const { apiUrl } = useStore().state;
    const axios = useAxios();

    const getAllPinsByRoundId = async (roundId: number) => {
        const res = await axios.get(`${apiUrl}/pins/round/${roundId}`);
        return res.data;
    };

    const updatePinMultiple = async (pins: any) => {
        const res = await axios.put(`${apiUrl}/pin/multiple/temp`, pins);
        return res.data;
    };

    const createPinMultiple = async (pins: any) => {
        const res = await axios.post(`${apiUrl}/pin/multiple/temp`, pins);
        return res.data;
    };

    const getAvailableLayers = async (courseSchema: string) => {
        const res = await axios.get(`${apiUrl}/pinzone/layers/${courseSchema}`);
        return res.data;
    };

    const mergePinsheets = async (
        pinsheetData: any,
        courseSchemaId: number,
    ) => {
        const res = await axios.post(
            `${apiUrl}/pinzone/merge-pinsheet/${courseSchemaId}`,
            pinsheetData,
        );
        return res.data;
    };

    const getAllTournamentPinsLayer = async (courseSchemaName: string) => {
        const res = await axios.get(
            `${apiUrl}/pinzone/tournament-layers/${courseSchemaName}`,
        );
        return res.data;
    };

    const updateMergedPinsheets = async (
        pinsheetData: any,
        courseSchemaId: number,
    ) => {
        const res = await axios.post(
            `${apiUrl}/pinzone/update-pinsheet/${courseSchemaId}`,
            pinsheetData,
        );
        return res.data;
    };

    const deletePinzoneLayer = async (
        pinzoneLayerId: number,
        courseSchemaId: number,
    ) => {
        const res = await axios.deleteId(
            `${apiUrl}/pinzone/tournament-layers/${pinzoneLayerId}/${courseSchemaId}`,
        );
    };

    const getGreenZoneLayers = async (
        courseSchema: string,
    ): Promise<GreenZonesLayers[]> => {
        const res = await axios.get(
            `${apiUrl}/pinzone/green-zone-layers/${courseSchema}`,
        );
        return res.data;
    };

    const getAllGreenGeom = async (
        courseSchemaId: number,
    ): Promise<PinzoneGeom[]> => {
        const res = await axios.get(
            `${apiUrl}/pinzone/greens/${courseSchemaId}`,
        );
        return res.data;
    };

    const getPinzoneExtent = async (courseSchemaId: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/pinzone/${courseSchemaId}/extent`,
        );
        return res.data;
    };

    const getAxisLinesData = async (pinData: any, courseSchemaName: any) => {
        const res = await axios.post(
            `${apiUrl}/pin/axis-line/${courseSchemaName}`,
            pinData,
        );
        return res.data;
    };

    const getGreenCenterLine = async (courseSchemaId: number, hole: number) => {
        const res = await axios.post(
            `${apiUrl}/temp/postgis/line/${courseSchemaId}/${hole}`,
            null,
        );
        return res.data;
    };

    const getPrevPinExclusionZones = async (prevPinsData: any) => {
        const res = await axios.post(
            `${apiUrl}/pin/exclusion/temp`,
            prevPinsData,
        );
        return res.data;
    };

    const getAllPinsByDate = async (pinFilter) => {
        const res = await axios.post(`${apiUrl}/pin/dates`, pinFilter);
        return res.data;
    };

    const getAllGrids = async (courseSchemaId: number) => {
        const res = await axios.get(`${apiUrl}/grid-data/${courseSchemaId}`);
        return res.data;
    };

    const createGrids = async (value: any) => {
        const res = await axios.post(`${apiUrl}/postgis/grid`, value);
        return res.data;
    };

    const getAllGreenGeoms = async (
        courseSchemaId: number,
    ): Promise<SharedGreenGeom[]> => {
        const res = await axios.get(
            `${apiUrl}/pinzone/shared-green/${courseSchemaId}`,
        );
        return res.data;
    };

    const checkIsSharedGreen = async (courseSchemaName: string) => {
        const res = await axios.get(
            `${apiUrl}/pinzone/is-shared-green/${courseSchemaName}`,
        );
        return res.data;
    };

    const getTournamentPinzoneLayers = async (
        courseSchemaId: number,
        tournamentPinLayer: string,
    ) => {
        const res = await axios.get(
            `${apiUrl}/pinzone/tournament-pin-layer/${courseSchemaId}/${tournamentPinLayer}`,
        );
        return res.data;
    };

    const getDefaultPinzoneMosaic = async (courseSchemaId) => {
        const res = await axios.get(
            `${apiUrl}/pinzone/mosaic/${courseSchemaId}`,
        );

        return res.data;
    };

    return {
        getAllPinsByDate,
        getAllGreenGeom,
        createPinMultiple,
        getAvailableLayers,
        updatePinMultiple,
        getAllPinsByRoundId,
        getPinzoneExtent,
        getGreenCenterLine,
        getPrevPinExclusionZones,
        getAllGrids,
        createGrids,
        getGreenZoneLayers,
        checkIsSharedGreen,
        getAllGreenGeoms,
        getTournamentPinzoneLayers,
        getDefaultPinzoneMosaic,
        getAxisLinesData,
        mergePinsheets,
        getAllTournamentPinsLayer,
        updateMergedPinsheets,
        deletePinzoneLayer,
    };
};
